import type { SampleDto } from '@/api/ApiClient';

/** Matches server AddToInventoryBatchLimits.MaxQuantity. */
export const MAX_ADD_TO_INVENTORY_QUANTITY = 50;

const DEFAULT_QUANTITY = 1;

/**
 * Parses UI quantity input. Invalid or empty values fall back to 1 (server treats &lt;= 0 as 1 as well).
 */
export function parseAddToInventoryQuantityInput(raw: string): number {
  const trimmed = raw.trim();
  if (!trimmed) {
    return DEFAULT_QUANTITY;
  }
  const parsed = Number.parseInt(trimmed, 10);
  if (!Number.isFinite(parsed)) {
    return DEFAULT_QUANTITY;
  }
  return parsed;
}

/** Client-side validation before submit; null when valid. */
export function validateAddToInventoryQuantity(quantity: number): string | null {
  if (!Number.isInteger(quantity)) {
    return 'Quantity must be a whole number.';
  }
  if (quantity < 1) {
    return 'Quantity must be at least 1.';
  }
  if (quantity > MAX_ADD_TO_INVENTORY_QUANTITY) {
    return `Quantity cannot exceed ${MAX_ADD_TO_INVENTORY_QUANTITY}.`;
  }
  return null;
}

export function formatAddToInventorySuccessMessage(samples: SampleDto[], inventoryKind: 'substrate' | 'grown'): string {
  const count = samples.length;
  const noun =
    inventoryKind === 'grown' ? (count === 1 ? 'sample' : 'samples') : count === 1 ? 'substrate' : 'substrates';
  const labels = samples.map((s) => s.sampleLabel).filter((label): label is string => Boolean(label));
  const labelSuffix =
    labels.length > 0
      ? `: ${labels.length <= 5 ? labels.join(', ') : `${labels.slice(0, 5).join(', ')} (+${labels.length - 5} more)`}`
      : '';
  if (count === 1) {
    return inventoryKind === 'grown'
      ? `Sample added to inventory${labelSuffix}.`
      : `Substrate added to inventory${labelSuffix}.`;
  }
  return `Added ${count} ${noun} to inventory${labelSuffix}.`;
}
